/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — HUD STYLESHEET
 * ===========================================================================
 *
 * One injected <style> block, scoped under `.sb`.
 *
 * RULES THIS SHEET OBEYS
 *  - ZERO external fonts. The capture harness aborts every off-origin request,
 *    so a webfont would silently vanish. Two stacks are used deliberately:
 *      display / names  -> Liberation Serif -> Times -> DejaVu Serif -> Georgia
 *      labels / numbers -> Liberation Sans  -> Helvetica -> Arial -> DejaVu
 *    Character comes from tracking, weight contrast, case and colour, not from
 *    a downloaded face. Every numeral bigger than 20 px is drawn on a canvas
 *    (see gfx.js) with a metal gradient and a cut outline, so the frame never
 *    shows a raw system glyph.
 *  - NO @keyframes / transitions on anything load-bearing. Playwright screen-
 *    shots with `animations:'disabled'`, which cancels infinite CSS animations
 *    back to their initial state — a CSS-animated glow would be invisible in
 *    every critic frame. All motion is driven from JS off `world.time.real`.
 *  - ONE scale knob: `--u`, a length set by resize() (1px at a 1600x900 design
 *    size, clamped 0.7 - 2.4). Every dimension is `calc(var(--u) * n)`, so the
 *    HUD is identical in proportion from 1280x720 to 4K.
 *  - pointer-events: none everywhere except an open modal.
 */

import { uiColor, factionOf, SIDE } from '../core/theme.js';

const P = factionOf(SIDE.PLAYER), E = factionOf(SIDE.ENEMY);

export const CSS = `
.sb{
  position:absolute; inset:0; overflow:hidden; pointer-events:none;
  --u:1px;
  --ink:#0a0908;
  --panel:rgba(11,9,7,.58);
  --panel2:rgba(6,5,4,.78);
  --line:rgba(228,190,116,.30);
  --gold:#e4bd72;
  --gold-hi:#fff2cf;
  --gold-dim:#96773c;
  --ally:${uiColor(SIDE.PLAYER)};
  --enemy:${uiColor(SIDE.ENEMY)};
  --paper:#f2e7cf;
  color:var(--paper);
  font-family:"Liberation Sans","Helvetica Neue",Helvetica,Arial,"DejaVu Sans",sans-serif;
  font-synthesis:none; -webkit-font-smoothing:antialiased;
  text-shadow:0 calc(var(--u)*1) calc(var(--u)*3) rgba(0,0,0,.85);
}
.sb.off{display:none}
.sb *{box-sizing:border-box}
.sb .ser{font-family:"Liberation Serif","Times New Roman","DejaVu Serif",Georgia,serif}
.sb .lab{
  font-size:calc(var(--u)*9.5); font-weight:700; letter-spacing:calc(var(--u)*3.2);
  text-transform:uppercase; color:rgba(238,226,200,.72);
}

/* --------------------------------------------------------- corner scrims */
/* A shipped HUD never floats on raw scene luminance. Four soft corner washes
   plus a floor gradient give every island a ground to sit on without reading
   as a panel. */
.sb .corners{position:absolute;inset:0;
  background:
    radial-gradient(ellipse calc(var(--u)*470) calc(var(--u)*250) at 0% 100%, rgba(3,3,4,.66), rgba(0,0,0,0) 72%),
    radial-gradient(ellipse calc(var(--u)*330) calc(var(--u)*280) at 100% 100%, rgba(3,3,4,.60), rgba(0,0,0,0) 72%),
    radial-gradient(ellipse calc(var(--u)*340) calc(var(--u)*230) at 100% 0%, rgba(3,3,4,.60), rgba(0,0,0,0) 72%),
    radial-gradient(ellipse calc(var(--u)*420) calc(var(--u)*300) at 0% 0%, rgba(3,3,4,.46), rgba(0,0,0,0) 72%),
    linear-gradient(to top, rgba(3,3,4,.30), rgba(0,0,0,0) 20%)}

/* ---------------------------------------------------------------- vignette */
.sb .vig{position:absolute;inset:0;opacity:1;
  background:radial-gradient(ellipse 78% 74% at 50% 46%, rgba(0,0,0,0) 38%, rgba(0,0,0,.30) 78%, rgba(0,0,0,.62) 100%)}
.sb .vig-hurt{position:absolute;inset:0;opacity:0;
  background:
    radial-gradient(ellipse 62% 58% at 50% 50%, rgba(0,0,0,0) 42%, rgba(150,16,10,.72) 100%),
    radial-gradient(ellipse 96% 92% at 50% 50%, rgba(0,0,0,0) 60%, rgba(60,0,0,.55) 100%)}
.sb .vig-low{position:absolute;inset:0;opacity:0;
  background:radial-gradient(ellipse 70% 66% at 50% 50%, rgba(0,0,0,0) 44%, rgba(122,10,8,.55) 100%)}
.sb .flashwipe{position:absolute;inset:0;opacity:0;background:rgba(255,236,196,1)}

/* --------------------------------------------------------------- fx canvas */
.sb .fxc{position:absolute;inset:0;width:100%;height:100%}

/* ============================================================ PLAYER PLATE */
.sb .plate{
  position:absolute; left:calc(var(--u)*30); bottom:calc(var(--u)*26);
  display:flex; align-items:flex-end; gap:calc(var(--u)*13);
}
.sb .pf{position:relative; width:calc(var(--u)*78); height:calc(var(--u)*78); flex:0 0 auto}
.sb .pf canvas{position:absolute; inset:0; width:100%; height:100%; display:block}
.sb .pcol{width:calc(var(--u)*306); padding-bottom:calc(var(--u)*2)}

.sb .prow{display:flex; align-items:baseline; gap:calc(var(--u)*10); margin-bottom:calc(var(--u)*5)}
.sb .pname{
  font-size:calc(var(--u)*19); letter-spacing:calc(var(--u)*2.6); font-weight:400;
  color:#fbf1dc; line-height:1;
  text-shadow:0 calc(var(--u)*2) calc(var(--u)*8) rgba(0,0,0,.9), 0 0 calc(var(--u)*16) rgba(226,168,72,.28);
}
.sb .ptitle{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*2.6);
  text-transform:uppercase; color:rgba(226,190,124,.66)}

/* bars: sheared parallelograms, the console-game tell */
.sb .barwrap{position:relative; display:flex; align-items:center; gap:calc(var(--u)*8)}
.sb .bar{
  position:relative; flex:1 1 auto; transform:skewX(-15deg); overflow:hidden;
  background:linear-gradient(180deg, rgba(2,2,1,.95) 0%, rgba(26,20,13,.9) 55%, rgba(6,5,3,.95) 100%);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(0,0,0,.95),
             inset 0 calc(var(--u)*2) calc(var(--u)*3) rgba(0,0,0,.75),
             0 calc(var(--u)*2) calc(var(--u)*8) rgba(0,0,0,.7);
}
.sb .bar>i{position:absolute; left:0; top:0; bottom:0; width:100%;
  transform-origin:0 50%; transform:scaleX(1); display:block}
.sb .bar>.segs{position:absolute; inset:0; display:block; pointer-events:none;
  background:repeating-linear-gradient(90deg,
    rgba(0,0,0,0) 0 calc(10% - var(--u)*2), rgba(0,0,0,.82) calc(10% - var(--u)*2) 10%)}
.sb .bar>.edge{position:absolute; inset:0; display:block; pointer-events:none;
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(236,206,142,.58),
             inset 0 calc(var(--u)*-2) calc(var(--u)*3) rgba(0,0,0,.55)}
.sb .bar>.gloss{position:absolute; left:0; right:0; top:0; height:40%; display:block;
  background:linear-gradient(180deg, rgba(255,255,255,.22), rgba(255,255,255,0))}

.sb .hp{height:calc(var(--u)*17)}
.sb .hp>.ghost{background:linear-gradient(180deg,#ffd7b6,#b23c26); opacity:.9}
.sb .hp>.fill{background:
  linear-gradient(180deg, rgba(255,255,255,.46) 0%, rgba(255,255,255,.05) 30%, rgba(0,0,0,.18) 72%, rgba(0,0,0,.42) 100%),
  linear-gradient(180deg,#c9f28c 0%,#79cf58 32%,#2e9440 74%,#14602a 100%)}
.sb .mu{height:calc(var(--u)*10); margin-top:calc(var(--u)*5)}
.sb .mu>.fill{background:
  linear-gradient(180deg, rgba(255,255,255,.5) 0%, rgba(255,255,255,.04) 32%, rgba(0,0,0,.20) 74%, rgba(0,0,0,.45) 100%),
  linear-gradient(180deg,#fff0b4 0%,#ffc94e 32%,#ef8c10 74%,#9c4707 100%)}
.sb .mu>.rdy{background:linear-gradient(90deg, rgba(255,255,255,0), rgba(255,248,214,.85), rgba(255,255,255,0)); opacity:0}

.sb .hpnum{
  width:calc(var(--u)*70); text-align:right; flex:0 0 auto;
  font-size:calc(var(--u)*11); font-weight:700; letter-spacing:calc(var(--u)*.6);
  color:#f6ecd6; font-variant-numeric:tabular-nums;
}
.sb .hpnum s{text-decoration:none; opacity:.45; font-size:calc(var(--u)*8.5); white-space:pre}
.sb .mulab{
  width:calc(var(--u)*70); text-align:right; flex:0 0 auto;
  font-size:calc(var(--u)*8); font-weight:700; letter-spacing:calc(var(--u)*3.4);
  text-transform:uppercase; color:rgba(226,190,124,.5);
}
.sb .mulab.on{color:#ffe9ab; text-shadow:0 0 calc(var(--u)*12) rgba(255,190,60,.9)}

/* ------------------------------------------------------------------ BRAVERY
   DEV_PLAN Step 3. Pips, not a bar: bravery is spent in whole points and the
   only question the player asks of it is "how many do I have" — a continuous
   fill answers that worse than four to ten discrete marks, and a bar would
   also read as a second musou gauge sitting under the first.
   A pip carries three states, and the third is the one that matters: lit
   (earned), dim (empty), and READY — lit and at or above the Battle Art's
   price, so the moment the expensive option opens is visible without counting. */
.sb .brv{display:flex; align-items:center; gap:calc(var(--u)*5); margin-top:calc(var(--u)*6)}
.sb .brv .pips{display:flex; gap:calc(var(--u)*4); flex:1 1 auto}
.sb .brv .pips u{display:block; width:calc(var(--u)*13); height:calc(var(--u)*7);
  text-decoration:none; transform:skewX(-18deg);
  background:rgba(10,7,5,.72);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.26)}
.sb .brv .pips u.on{
  background:linear-gradient(180deg,#ffe6c0 0%,#e8a33f 44%,#a85f15 100%);
  box-shadow:0 0 calc(var(--u)*7) rgba(255,186,88,.55),
             inset 0 0 0 calc(var(--u)*1) rgba(255,226,170,.55)}
.sb .brv .pips u.art{
  background:linear-gradient(180deg,#fff4d8 0%,#ffc866 44%,#d07f1c 100%);
  box-shadow:0 0 calc(var(--u)*11) rgba(255,206,120,.85),
             inset 0 0 0 calc(var(--u)*1) rgba(255,244,214,.7)}
.sb .brvlab{
  width:calc(var(--u)*70); text-align:right; flex:0 0 auto;
  font-size:calc(var(--u)*8); font-weight:700; letter-spacing:calc(var(--u)*3.4);
  text-transform:uppercase; color:rgba(226,190,124,.5);
}
.sb .brvlab.on{color:#ffe9ab; text-shadow:0 0 calc(var(--u)*12) rgba(255,190,60,.9)}

/* =============================================================== KO COUNTER */
.sb .ko{position:absolute; right:calc(var(--u)*30); top:calc(var(--u)*22); text-align:right}
.sb .ko .num{display:block; width:calc(var(--u)*236); height:calc(var(--u)*88); margin-left:auto;
  transform-origin:100% 60%}
.sb .ko .rule{height:calc(var(--u)*1.5); margin:calc(var(--u)*3) 0 calc(var(--u)*5) auto;
  width:calc(var(--u)*118);
  background:linear-gradient(90deg, rgba(228,190,116,0), rgba(228,190,116,.8))}
.sb .ko .kolab{font-size:calc(var(--u)*12.5); font-weight:700; letter-spacing:calc(var(--u)*8.5);
  text-transform:uppercase; color:rgba(248,238,216,.95); padding-left:calc(var(--u)*9)}
.sb .ko .clock{margin-top:calc(var(--u)*10); font-size:calc(var(--u)*12); font-weight:700;
  letter-spacing:calc(var(--u)*2.6); color:rgba(240,206,144,.88); font-variant-numeric:tabular-nums}
.sb .ko .clock em{font-style:normal; font-size:calc(var(--u)*8); letter-spacing:calc(var(--u)*2.6);
  opacity:.6; margin-right:calc(var(--u)*6)}

/* ============================================================== HIT CHAIN */
.sb .chain{position:absolute; right:calc(var(--u)*46); top:44%; text-align:right; opacity:0}
.sb .chain .num{display:block; width:calc(var(--u)*230); height:calc(var(--u)*92); margin-left:auto;
  transform-origin:100% 60%}
.sb .chain .chlab{font-size:calc(var(--u)*11); font-weight:700; letter-spacing:calc(var(--u)*6.5);
  text-transform:uppercase; color:#ffd58a; margin-top:calc(var(--u)*-6); padding-left:calc(var(--u)*8);
  text-shadow:0 0 calc(var(--u)*14) rgba(255,150,30,.7)}
.sb .chain .best{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*2.8);
  color:rgba(226,190,124,.55); margin-top:calc(var(--u)*4)}

/* ============================================================ OFFICER BAR */
.sb .off{position:absolute; left:50%; top:calc(var(--u)*20); width:calc(var(--u)*452);
  margin-left:calc(var(--u)*-226); opacity:0}
.sb .off .plaque{
  padding:calc(var(--u)*7) calc(var(--u)*16) calc(var(--u)*8);
  background:linear-gradient(180deg, rgba(14,10,8,.72), rgba(6,5,4,.58));
  clip-path:polygon(calc(var(--u)*14) 0, 100% 0, 100% calc(100% - var(--u)*14),
                    calc(100% - var(--u)*14) 100%, 0 100%, 0 calc(var(--u)*14));
  box-shadow:0 calc(var(--u)*3) calc(var(--u)*14) rgba(0,0,0,.6);
}
.sb .off .otop{display:flex; align-items:baseline; justify-content:center; gap:calc(var(--u)*12)}
.sb .off .oname{font-size:calc(var(--u)*20); letter-spacing:calc(var(--u)*3.4); color:#fff0d4; line-height:1.05}
.sb .off .obar{position:relative; height:calc(var(--u)*11); margin-top:calc(var(--u)*7);
  transform:skewX(-15deg); overflow:hidden;
  background:linear-gradient(180deg, rgba(4,3,2,.94), rgba(22,14,10,.9));
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(0,0,0,.9)}
.sb .off .obar>i{position:absolute; left:0; top:0; bottom:0; width:100%;
  transform-origin:0 50%; display:block}
.sb .off .obar>.ghost{background:linear-gradient(180deg,#ffd8c0,#8c2418); opacity:.8}
.sb .off .obar>.fill{background:linear-gradient(180deg,#ffb59a 0%,#ff6a4a 30%,#cf2c18 76%,#7d1408 100%)}
.sb .off .obar>.notch{position:absolute; top:0; bottom:0; width:calc(var(--u)*1.5); display:block;
  background:rgba(0,0,0,.8); box-shadow:0 0 0 calc(var(--u)*.5) rgba(255,220,170,.28)}
.sb .off .obot{display:flex; align-items:center; justify-content:space-between; margin-top:calc(var(--u)*6)}
.sb .off .otitle{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3);
  text-transform:uppercase; color:rgba(232,196,140,.62)}
.sb .off .pips{display:flex; gap:calc(var(--u)*5); align-items:center}
.sb .off .pips i{width:calc(var(--u)*7); height:calc(var(--u)*7); display:block; transform:rotate(45deg);
  background:rgba(255,220,170,.14); box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(255,214,160,.45)}
.sb .off .pips i.on{background:#ff7a55; box-shadow:0 0 calc(var(--u)*9) rgba(255,90,50,.9),
  inset 0 0 0 calc(var(--u)*1) rgba(255,226,190,.8)}
.sb .off .vuln{position:absolute; left:0; right:0; top:calc(var(--u)*-15); text-align:center;
  font-size:calc(var(--u)*9); font-weight:700; letter-spacing:calc(var(--u)*5);
  color:#ffe07a; opacity:0; text-shadow:0 0 calc(var(--u)*12) rgba(255,170,40,.9)}
.sb .off .odist{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*2);
  color:rgba(226,190,124,.5); font-variant-numeric:tabular-nums}

/* ================================================================ MINIMAP */
.sb .map{position:absolute; right:calc(var(--u)*28); bottom:calc(var(--u)*26);
  width:calc(var(--u)*196); height:calc(var(--u)*196)}
.sb .map canvas{position:absolute; inset:0; width:100%; height:100%; display:block;
  filter:drop-shadow(0 calc(var(--u)*3) calc(var(--u)*10) rgba(0,0,0,.65))}
.sb .map .mtag{position:absolute; left:0; right:0; bottom:calc(var(--u)*-15); text-align:center;
  font-size:calc(var(--u)*8); font-weight:700; letter-spacing:calc(var(--u)*3.4);
  text-transform:uppercase; color:rgba(226,190,124,.55)}
.sb .map .tide{position:absolute; left:calc(var(--u)*8); right:calc(var(--u)*8);
  top:calc(var(--u)*-15); height:calc(var(--u)*6);
  background:rgba(0,0,0,.85);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(232,196,124,.4), 0 calc(var(--u)*2) calc(var(--u)*6) rgba(0,0,0,.6)}
/* The morale tide. These four were hardcoded blue-left / red-right, which was
   silently correct only while the player happened to be the blue side. The
   moment the factions repainted, the bar was telling the player their own
   morale in the enemy's colour. Derived now, so it cannot go stale again. */
.sb .map .tide i{position:absolute; left:0; top:0; bottom:0; display:block; width:50%;
  background:linear-gradient(180deg,${P.ui.hi},${P.ui.lo})}
.sb .map .tide u{position:absolute; right:0; top:0; bottom:0; display:block; width:50%;
  background:linear-gradient(180deg,${E.ui.hi},${E.ui.lo})}
.sb .map .tide::after{content:""; position:absolute; left:50%; top:calc(var(--u)*-2); bottom:calc(var(--u)*-2);
  width:calc(var(--u)*1.5); margin-left:calc(var(--u)*-.75); background:rgba(255,238,200,.85)}
.sb .map .tlab{position:absolute; left:0; right:0; top:calc(var(--u)*-27); display:flex;
  justify-content:space-between; font-size:calc(var(--u)*7.5); font-weight:700;
  letter-spacing:calc(var(--u)*2.4); text-transform:uppercase}
.sb .map .tlab b{color:${P.ui.label}; font-weight:700}
.sb .map .tlab u{color:${E.ui.label}; text-decoration:none}

/* ============================================================ MISSION LOG */
.sb .log{position:absolute; left:calc(var(--u)*30); top:calc(var(--u)*112); width:calc(var(--u)*290)}
.sb .log .line{display:flex; align-items:flex-start; gap:calc(var(--u)*8);
  margin-bottom:calc(var(--u)*7); opacity:0; will-change:opacity,transform}
.sb .log .line b{flex:0 0 auto; width:calc(var(--u)*6); height:calc(var(--u)*6); margin-top:calc(var(--u)*4);
  transform:rotate(45deg); background:var(--gold); box-shadow:0 0 calc(var(--u)*8) rgba(230,180,90,.7)}
.sb .log .line span{font-size:calc(var(--u)*11.5); font-weight:400; letter-spacing:calc(var(--u)*.9);
  line-height:1.34; color:#efe3ca}
.sb .log .line.warn span{color:#ffcf9a}
.sb .log .line.good span{color:#c6e9a8}
.sb .log .line.bad span{color:#ff9c86}

/* ================================================================= BANNER */
.sb .ban{position:absolute; left:0; right:0; top:31%; text-align:center; opacity:0}
.sb .ban .rule{height:calc(var(--u)*1.5); margin:0 auto; width:0;
  background:linear-gradient(90deg, rgba(228,190,116,0), rgba(255,236,196,.95), rgba(228,190,116,0))}
.sb .ban .bt{font-size:calc(var(--u)*46); letter-spacing:calc(var(--u)*11); color:#fff5df;
  padding:calc(var(--u)*10) 0 calc(var(--u)*8); line-height:1.05;
  text-shadow:0 calc(var(--u)*3) calc(var(--u)*18) rgba(0,0,0,.9), 0 0 calc(var(--u)*40) rgba(240,180,80,.45)}
.sb .ban .bs{font-size:calc(var(--u)*11); font-weight:700; letter-spacing:calc(var(--u)*7.5);
  text-transform:uppercase; color:rgba(236,206,152,.86); padding-bottom:calc(var(--u)*11)}

/* ============================================================== DEV STATS */
.sb .stat{position:absolute; left:calc(var(--u)*10); top:calc(var(--u)*8);
  font:calc(var(--u)*9.5)/1.5 "Liberation Mono","DejaVu Sans Mono",ui-monospace,monospace;
  color:rgba(226,214,190,.42); letter-spacing:calc(var(--u)*.4)}

/* ================================================================= MODALS */
.sb .modal{position:absolute; inset:0; display:none; pointer-events:none;
  align-items:center; justify-content:center; flex-direction:column}
.sb .modal.open{display:flex; pointer-events:auto}
.sb .scrim{position:absolute; inset:0;
  background:radial-gradient(ellipse 90% 80% at 50% 45%, rgba(8,6,5,.72), rgba(3,2,2,.93))}
.sb .modal .body{position:relative; text-align:center; padding:calc(var(--u)*10)}

.sb .mtitle{font-size:calc(var(--u)*62); letter-spacing:calc(var(--u)*16); color:#fbf0d6; line-height:1;
  text-shadow:0 calc(var(--u)*4) calc(var(--u)*26) rgba(0,0,0,.9), 0 0 calc(var(--u)*54) rgba(228,160,60,.4)}
.sb .msub{font-size:calc(var(--u)*11); font-weight:700; letter-spacing:calc(var(--u)*8.5);
  text-transform:uppercase; color:rgba(230,196,140,.72); margin-top:calc(var(--u)*16)}
.sb .mrule{height:calc(var(--u)*1.5); width:calc(var(--u)*440); margin:calc(var(--u)*22) auto;
  background:linear-gradient(90deg, rgba(228,190,116,0), rgba(228,190,116,.85), rgba(228,190,116,0))}
.sb .mrule.sm{width:calc(var(--u)*260); margin:calc(var(--u)*14) auto}

.sb .menu{display:flex; flex-direction:column; gap:calc(var(--u)*3); align-items:center;
  margin-top:calc(var(--u)*14)}
.sb .btn{
  position:relative; pointer-events:auto; cursor:pointer; user-select:none;
  min-width:calc(var(--u)*300); padding:calc(var(--u)*11) calc(var(--u)*26);
  font-size:calc(var(--u)*12.5); font-weight:700; letter-spacing:calc(var(--u)*5.5);
  text-transform:uppercase; color:rgba(240,228,204,.8); background:rgba(255,240,210,.03);
  clip-path:polygon(calc(var(--u)*11) 0, 100% 0, calc(100% - var(--u)*11) 100%, 0 100%);
  border:0; text-align:center; font-family:inherit;
}
.sb .btn::after{content:""; position:absolute; left:calc(var(--u)*11); right:calc(var(--u)*11);
  bottom:calc(var(--u)*4); height:calc(var(--u)*1); background:rgba(228,190,116,.18)}
.sb .btn.on,.sb .btn:hover{color:#fff6e2; background:rgba(255,214,140,.11)}
.sb .btn.on::after,.sb .btn:hover::after{background:rgba(255,226,160,.75);
  box-shadow:0 0 calc(var(--u)*10) rgba(255,190,90,.6)}

.sb .rows{margin-top:calc(var(--u)*8); width:calc(var(--u)*470); text-align:left}
.sb .row{display:flex; align-items:center; gap:calc(var(--u)*14); padding:calc(var(--u)*9) 0;
  border-bottom:calc(var(--u)*1) solid rgba(228,190,116,.13)}
.sb .row>.k{flex:0 0 calc(var(--u)*150); font-size:calc(var(--u)*10); font-weight:700;
  letter-spacing:calc(var(--u)*3.2); text-transform:uppercase; color:rgba(232,206,158,.78)}
.sb .row>.v{flex:1 1 auto; display:flex; align-items:center; gap:calc(var(--u)*6)}
.sb .seg{pointer-events:auto; cursor:pointer; padding:calc(var(--u)*5) calc(var(--u)*9);
  font-size:calc(var(--u)*9); font-weight:700; letter-spacing:calc(var(--u)*2.2); text-transform:uppercase;
  color:rgba(226,212,186,.55); background:rgba(255,240,210,.04); font-family:inherit; border:0}
.sb .seg.on{color:#0e0b07; background:linear-gradient(180deg,#ffe6ac,#d9a a4a); background:linear-gradient(180deg,#ffe6ac,#d9a44a)}
.sb .slide{position:relative; pointer-events:auto; cursor:pointer; flex:1 1 auto;
  height:calc(var(--u)*13); display:flex; align-items:center}
.sb .slide .trk{position:absolute; left:0; right:0; height:calc(var(--u)*5);
  background:rgba(0,0,0,.6); box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.22)}
.sb .slide .fl{position:absolute; left:0; height:calc(var(--u)*5); width:50%;
  background:linear-gradient(90deg,#a97c2e,#ffe2a2)}
.sb .slide .kn{position:absolute; width:calc(var(--u)*9); height:calc(var(--u)*13); left:50%;
  margin-left:calc(var(--u)*-4.5); background:#ffe9be; transform:skewX(-14deg);
  box-shadow:0 0 calc(var(--u)*8) rgba(255,190,90,.55)}
.sb .row>.n{flex:0 0 calc(var(--u)*54); text-align:right; font-size:calc(var(--u)*10.5); font-weight:700;
  color:rgba(240,228,204,.8); font-variant-numeric:tabular-nums}

/* ================================================================ RESULTS */
.sb .res>.body{width:calc(var(--u)*620); max-width:100%}
.sb .res .grid{display:flex; justify-content:center; gap:calc(var(--u)*10);
  margin-top:calc(var(--u)*18)}
.sb .res .cell{flex:1 1 0; padding:calc(var(--u)*13) calc(var(--u)*6) calc(var(--u)*11);
  background:linear-gradient(180deg, rgba(255,236,200,.05), rgba(255,236,200,.01));
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.16)}
.sb .res .cell canvas{display:block; width:calc(var(--u)*130); height:calc(var(--u)*54); margin:0 auto}
.sb .res .cell .k{margin-top:calc(var(--u)*6); font-size:calc(var(--u)*8.5); font-weight:700;
  letter-spacing:calc(var(--u)*3.2); text-transform:uppercase; color:rgba(226,190,124,.6)}
.sb .res .rank{margin-top:calc(var(--u)*22); display:flex; align-items:center; justify-content:center;
  gap:calc(var(--u)*18)}
.sb .res .rank .rk{font-size:calc(var(--u)*86); letter-spacing:calc(var(--u)*6); line-height:1;
  color:#ffeaba; text-shadow:0 calc(var(--u)*4) calc(var(--u)*22) rgba(0,0,0,.9),
    0 0 calc(var(--u)*46) rgba(255,180,60,.55)}
.sb .res .rank .rl{text-align:left}
.sb .res .rank .rl b{display:block; font-size:calc(var(--u)*13); font-weight:700;
  letter-spacing:calc(var(--u)*5); text-transform:uppercase; color:#f4e4c2}
.sb .res .rank .rl span{display:block; font-size:calc(var(--u)*9.5); letter-spacing:calc(var(--u)*2.4);
  color:rgba(226,190,124,.62); margin-top:calc(var(--u)*4)}

/* ============================================================ TITLE SCREEN */
.sb .title .body{margin-top:calc(var(--u)*-20)}
.sb .title .crest{display:block; width:calc(var(--u)*118); height:calc(var(--u)*118); margin:0 auto calc(var(--u)*10)}
.sb .hint{margin-top:calc(var(--u)*26); font-size:calc(var(--u)*10); font-weight:700;
  letter-spacing:calc(var(--u)*5); text-transform:uppercase; color:rgba(232,208,160,.5)}
.sb .keys{position:absolute; left:0; right:0; bottom:calc(var(--u)*24); text-align:center;
  font-size:calc(var(--u)*9); font-weight:700; letter-spacing:calc(var(--u)*2.6);
  text-transform:uppercase; color:rgba(226,206,168,.38)}
.sb .keys em{font-style:normal; color:rgba(255,226,166,.72)}
`;
